import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../translations';

const TeamPage = () => {
  const { language } = useLanguage();

  return (
    <main className="min-h-screen bg-[#FCF7EC] pt-20">
      {/* How We Work + Our Approach side by side */}
      <section className="py-16 md:py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-10"
          >
            <p className="text-xs tracking-[0.2em] uppercase font-sans text-teal mb-2">
              {t('team.label', language)}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 md:gap-16">
            {/* How We Work - Left */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h2 className="text-3xl md:text-4xl font-serif text-spruce mb-8 uppercase tracking-wide">
                {t('team.howWeWorkTitle', language)}
              </h2>
              <div className="space-y-5 text-deep-teal font-sans leading-relaxed text-sm md:text-base">
                <p className="font-serif text-spruce text-base italic">
                  {t('team.howWeWorkP1', language)}
                </p>
                <p>
                  {t('team.howWeWorkP2', language)}
                </p>
                <p className="font-serif text-spruce text-base italic">
                  {t('team.howWeWorkP2b', language)}
                </p>
                <p>
                  {t('team.howWeWorkP3', language)}
                </p>
                <p className="font-serif text-spruce italic">
                  {t('team.howWeWorkP4', language)}
                </p>
                <p>
                  {t('team.howWeWorkP5', language)}
                </p>
              </div>
            </motion.div>

            {/* Our Approach - Right */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h2 className="text-3xl md:text-4xl font-serif text-spruce mb-8 uppercase tracking-wide">
                {t('team.ourApproachTitle', language)}
              </h2>
              <div className="space-y-5 text-deep-teal font-sans leading-relaxed text-sm md:text-base">
                <p>
                  {t('team.ourApproachP1', language)}
                </p>
                <p>
                  {t('team.ourApproachP2', language)}
                </p>
                <div className="pt-4">
                  <p className="text-xs tracking-[0.2em] uppercase font-sans text-teal mb-3">
                    {language === 'fr' ? 'Nos Pratiques' : language === 'pt' ? 'As Nossas Práticas' : 'Our Practices'}
                  </p>
                  <div className="grid grid-cols-2 gap-x-6">
                    <ul className="space-y-1.5 text-deep-teal font-sans text-sm">
                      <li>Bioresonance</li>
                      <li>Bodywork</li>
                      <li>Massages</li>
                      <li>Breathwork</li>
                      <li>{language === 'fr' ? 'Travail Énergétique' : language === 'pt' ? 'Trabalho Energético' : 'Energy Work'}</li>
                      <li>Nutrition</li>
                    </ul>
                    <ul className="space-y-1.5 text-deep-teal font-sans text-sm">
                      <li>{language === 'fr' ? 'Guérison Sonore' : language === 'pt' ? 'Cura Sonora' : 'Sound Healing'}</li>
                      <li>{language === 'fr' ? 'Thérapie Aquatique Watsu' : language === 'pt' ? 'Terapia Aquática Watsu' : 'Water Therapy Watsu'}</li>
                      <li>{language === 'fr' ? 'Travail Somatique' : language === 'pt' ? 'Trabalho Somático' : 'Somatic Work'}</li>
                      <li>{language === 'fr' ? 'Pratiques Ancestrales et Élémentaires' : language === 'pt' ? 'Práticas Ancestrais e Elementais' : 'Ancestral and Elemental Practices'}</li>
                      <li>{language === 'fr' ? 'Rituels et Initiations' : language === 'pt' ? 'Rituais e Iniciações' : 'Rituals and Initiations'}</li>
                      <li>{language === 'fr' ? 'Conscience et Éveil' : language === 'pt' ? 'Consciência e Despertar' : 'Consciousness Awareness'}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Julie's Full Bio */}
      <section className="py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-[280px_1fr] gap-8 items-start">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_mindful-container/artifacts/ohyh33mv_0S0A3577.jpg"
                alt="Julie Aurora Boulanger"
                className="w-full max-h-[400px] object-cover object-top rounded-lg ambient-shadow mb-4"
              />
              
              {/* Modalities */}
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-sans text-teal mb-2">
                  {t('team.trainedIn', language)}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  <span className="text-xs font-sans text-deep-teal">
                    {t('team.modalities', language)}
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl md:text-3xl font-serif text-spruce mb-4 uppercase tracking-wide">
                Julie Aurora Boulanger
              </h2>

              <div className="space-y-3 text-deep-teal font-sans leading-relaxed text-sm">
                <h3 className="text-base font-serif text-spruce uppercase tracking-wide">
                  {t('team.fromPerformance', language)}
                </h3>

                <div>
                  <h4 className="font-serif text-spruce mb-1 italic text-sm">{t('team.immersionTitle', language)}</h4>
                  <p>
                    {t('team.immersionDesc', language)}<br />
                    {t('team.immersionList', language)}<br />
                    {t('team.immersionList2', language)}
                  </p>
                </div>

                <div>
                  <h4 className="font-serif text-spruce mb-1 italic text-sm">{t('team.catalystTitle', language)}</h4>
                  <p>
                    {t('team.catalystDesc1', language)}<br />
                    {t('team.catalystDesc2', language)}<br />
                    {t('team.catalystDesc3', language)}
                  </p>
                </div>

                <div>
                  <h4 className="font-serif text-spruce mb-1 italic text-sm">{t('team.synthesisTitle', language)}</h4>
                  <p>
                    {t('team.synthesisDesc1', language)}<br />
                    {t('team.synthesisDesc2', language)}
                  </p>
                  <a
                    href="https://www.julieauroraboulanger.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-2 text-teal underline text-sm font-sans hover:text-spruce transition-colors"
                  >
                    www.julieauroraboulanger.com
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Other Healers Grid */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            {/* Jeremy Agnew */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_mindful-container/artifacts/5rt508ry_WhatsApp%20Image%202026-04-14%20at%2015.19.54%20%282%29.jpeg"
                alt="Jeremy Agnew"
                className="w-full aspect-square object-cover object-top rounded-lg mb-4"
              />
              <h3 className="font-serif text-spruce text-lg uppercase tracking-wide">Jeremy Agnew</h3>
              <p className="text-xs font-sans text-deep-teal mt-1">Somatic Facilitator | Coach</p>
            </motion.div>

            {/* Inez Aires */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_mindful-container/artifacts/m5mmuke8_WhatsApp%20Image%202026-04-15%20at%2021.17.50.jpeg"
                alt="Inez Aires"
                className="w-full aspect-square object-cover object-top rounded-lg mb-4"
              />
              <h3 className="font-serif text-spruce text-lg uppercase tracking-wide">Inez Aires</h3>
              <p className="text-xs font-sans text-deep-teal mt-1">Bodyworker | Elemental Work | Fire Keeper</p>
            </motion.div>

            {/* Ines Pessoa */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="text-center"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_mindful-container/artifacts/ze8b2bap_Screenshot%202026-04-16%20at%2000.11.00.png"
                alt="Ines Pessoa"
                className="w-full aspect-square object-cover object-top rounded-lg mb-4"
              />
              <h3 className="font-serif text-spruce text-lg uppercase tracking-wide">Ines Pessoa</h3>
              <p className="text-xs font-sans text-deep-teal mt-1">Biofeedback Therapist</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-serif text-spruce mb-6">
            {t('team.readyToMeet', language)}
          </h2>
          <p className="text-deep-teal font-sans mb-8">
            {t('team.readyToMeetDesc', language)}
          </p>
          <Link
            to="/contact"
            className="inline-block btn-primary px-10 py-4 font-sans text-base tracking-wide rounded-full"
          >
            {t('nav.bookCall', language)}
          </Link>
        </div>
      </section>
    </main>
  );
};

export default TeamPage;
